/* eslint-disable check-file/filename-blocklist */
import type { ADDoc, ADNode } from '@atlaskit/editor-common/utils/validator';

export const parseMacroOutputAdf = (macroOutput: string): ADDoc | undefined => {
	try {
		if (macroOutput) {
			const parsed = JSON.parse(macroOutput);
			if (parsed.version === 1 && parsed.type === 'doc' && parsed.content) {
				return parsed as ADDoc;
			}
		}
	} catch (e) {
		// do nothing in case of parsing error, which would return `undefined`
	}
};

export const getMacroId = (node: any): any =>
	node?.parameters?.macroMetadata?.macroId?.value ||
	node?.attrs?.parameters?.macroMetadata?.macroId?.value ||
	node?.localId ||
	node?.attrs?.localId;
export const getMacroParam = (node: any, name: string): any =>
	node?.parameters?.macroParams?.[name]?.value;

const EXTENSION_NODE_TYPES = new Set([
	'extension',
	'inlineExtension',
	'bodiedExtension',
	'multiBodiedExtension',
]);
const MACRO_EXTENSION_TYPE = 'com.atlassian.confluence.macro.core';
const LEGACY_CONTENT_EXTENSION_TYPE = 'com.atlassian.confluence.migration';
const LEGACY_CONTENT_EXTENSION_KEY = 'legacy-content';

export const injectParentIdIntoAdf = (
	adf: ADNode | ADNode[],
	sourceContentId: string,
): ADNode | ADNode[] => {
	if (Array.isArray(adf)) {
		return adf.map((node) => injectParentIdIntoNode(node, sourceContentId));
	}
	return injectParentIdIntoNode(adf, sourceContentId);
};

const injectParentIdIntoNode = (node: ADNode, sourceContentId: string): ADNode => {
	if (!node || typeof node !== 'object') {
		return node;
	}

	let updatedNode = node;

	if (
		node.type &&
		EXTENSION_NODE_TYPES.has(node.type) &&
		node.attrs?.extensionType === MACRO_EXTENSION_TYPE &&
		!node.attrs?.parameters?.macroParams?._parentId
	) {
		updatedNode = {
			...node,
			attrs: {
				...node.attrs,
				parameters: {
					...node.attrs?.parameters,
					macroParams: {
						...node.attrs?.parameters?.macroParams,
						_parentId: { value: sourceContentId },
					},
				},
			},
		};
	}

	const nestedContent =
		node.attrs?.extensionType === LEGACY_CONTENT_EXTENSION_TYPE &&
		node.attrs?.extensionKey === LEGACY_CONTENT_EXTENSION_KEY
			? node.attrs?.parameters?.nestedContent
			: undefined;
	if (nestedContent && typeof nestedContent === 'object') {
		updatedNode = {
			...updatedNode,
			attrs: {
				...updatedNode.attrs,
				parameters: {
					...updatedNode.attrs?.parameters,
					nestedContent: injectParentIdIntoAdf(nestedContent as ADNode, sourceContentId),
				},
			},
		};
	}

	if (updatedNode.content && Array.isArray(updatedNode.content)) {
		updatedNode = {
			...updatedNode,
			content: updatedNode.content.map((child: ADNode) =>
				injectParentIdIntoNode(child, sourceContentId),
			),
		};
	}

	return updatedNode;
};
